// Shell of the signed-out screens (sign in, forgot / reset password, accept invitation): the Dosen
// 3.0 dark surface with one card. Built on the design tokens (components/ui/theme/tokens.css) and
// native inputs, so the legacy MUI theme overrides do not leak into these pages.
import React from 'react';
import { Box, Typography } from '@mui/material';
import logo from '../../assets/images/brand/dosen/white-logo.svg';

interface AuthShellProps {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** below the card, e.g. the way back to sign-in */
  footer?: React.ReactNode;
}

const AuthShell: React.FC<AuthShellProps> = ({ title, subtitle, children, footer }) => (
  <Box
    component="main"
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      px: 2,
      py: 4,
      boxSizing: 'border-box',
      fontFamily: 'Inter, sans-serif',
      color: 'var(--text-text-primary, #fdfdfd)',
      // The brand's ember, low and wide behind the logo — the page's only decoration.
      background:
        'radial-gradient(520px 260px at 50% -40px, rgba(247, 148, 32, 0.16), rgba(241, 90, 38, 0.05) 55%, transparent 75%), var(--stroke-solid-black, #0f0f0f)'
    }}
  >
    <Box component="img" src={logo} alt="Dosen" sx={{ width: 132, height: 'auto', display: 'block' }} />
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        boxSizing: 'border-box',
        p: { xs: '24px 20px', sm: '32px' },
        borderRadius: '12px',
        border: '1px solid var(--stroke-border-border-dim, #2a2a2a)',
        background: 'var(--fill-card-card-default, #151515)'
      }}
    >
      <Typography
        component="h1"
        sx={{ m: 0, fontSize: 22, lineHeight: '28px', fontWeight: 600, color: 'var(--text-text-primary, #fdfdfd)' }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          component="p"
          sx={{ m: '8px 0 0', fontSize: 14, lineHeight: '22px', color: 'var(--text-text-secondary, #b6b6b6)' }}
        >
          {subtitle}
        </Typography>
      ) : null}
      <Box sx={{ mt: '24px' }}>{children}</Box>
    </Box>
    {footer ? (
      <Box sx={{ fontSize: 13, color: 'var(--text-text-tertiary, #868686)', textAlign: 'center' }}>{footer}</Box>
    ) : null}
  </Box>
);

export default AuthShell;
